import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import ProviderBuilder from 'assets/providers/ProviderBuilder';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { date, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { normalizeCountryCode } from 'assets/utils/helpers/NormalizationHelper';
import { isEmail, isEmailDomain, isValidValue } from 'assets/utils/parsersAndValidation/Validators';
import { defaultOffset, HttpStatus } from 'config/Api.Config';
import { Dictionary, first, flatMap, groupBy, isEmpty, keys, omit, set, sortBy, uniq, values } from 'lodash';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import claimJobResetStatusAndDeleteAssociatedPaymentItemsApi from 'models/claims/claimJob/resetStatusAndDeleteAssociatedPaymentItems/Api.ClaimJob.ResetStatusAndDeleteAssociatedPaymentItems';
import claimJobSaveApi from 'models/claims/claimJob/save/Api.ClaimJob.Save';
import claimJobSetStatusApi from 'models/claims/claimJob/setStatus/Api.ClaimJob.SetStatus';
import ClaimJobComment from 'models/claims/claimJobComment/ClaimJobComment';
import claimJobCommentDeleteApi from 'models/claims/claimJobComment/delete/Api.ClaimJobComment.Delete';
import claimJobCommentSaveApi from 'models/claims/claimJobComment/save/Api.ClaimJobComment.Save';
import ClaimJobItem from 'models/claims/claimJobItem/Model.ClaimJobItem';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import ClaimType from 'models/claims/claimType/Model.ClaimType';
import LossCode from 'models/claims/lossCode/Model.LossCode';
import Payee from 'models/claims/payee/Model.Payee';
import PayeeType from 'models/claims/payeeType/Model.PayeeType';
import ClaimJobEmailTemplate from 'models/core/claimJobEmailTemplate/Model.ClaimJobEmailTemplate';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import Currency from 'models/enums/Currency';
import LineItem from 'models/enums/LineItem';
import PaymentStatus from 'models/enums/PaymentStatus';
import ProductType from 'models/enums/ProductType';
import ClaimJobPaymentItem from 'models/finance/claimJobPaymentItem/Model.ClaimJobPaymentItem';
import claimJobPaymentItemSaveApi from 'models/finance/claimJobPaymentItem/save/Api.ClaimJobPaymentItem.Save';
import Insurer from 'models/productManagement/insurer/Model.Insurer';
import Product from 'models/productManagement/product/Model.Product';
import { useEffect, useMemo, useState } from 'react';
import { useClaimEdit } from '../../ClaimEdit.Hooks';

export enum ClaimJobAction {
  EDIT = 'edit',
  DELETE = 'delete',
}

export type UseClaimJobProps = {
  claimJobId?: number;
};

export type ClaimJobTableProps = {
  items: Array<{ item: Utils.FormData<Model.IClaimJobItem>; index: number }>;
  updateJobItem(data: Utils.FormData<Model.IClaimJobItem>, index?: number): void;
  removeJobItem(data: Utils.FormData<Model.IClaimJobItem>, index: number): void;
  lossCodes: Array<LossCode>;
  currency: string;
  product?: Product;
  insurerHeaders?: string[];
  isWarrantyProduct?: boolean;
  isAddDisabled?: boolean;
  isEditDisabled?: boolean;
  isDeleteDisabled?: boolean;
  setNewPayeeId?: React.Dispatch<React.SetStateAction<number>>;
};
export default function useClaimJobEditCore({ claimJobId }: UseClaimJobProps) {
  const { permissions, userId } = useAuthStore();
  const {
    claim,
    reloadClaim,
    product,
    contract,
    isClaimAdmin,
    isClaimOwner,
    isEditable,
    isClaimJobEditableByStatus,
    isClaimJobItemEditable,
    hasAnyClaimJobPermission,
    canEditAdjustedAmt,
    canEditInsurerAdjudications,
    payeeRestriction,
    emailAction,
    setEmailAction,
  } = useClaimEdit();

  const claimTypesApi = useApi({
    action: ClaimType.list,
    body: {
      productId: [product?.id],
      // dealerId: user?.dealers?.map((it) => it.id) ?? []
    },
  });
  const claimTypes = claimTypesApi.payload?.data;

  const { lang } = getLocales();
  const [newPayeeId, setNewPayeeId] = useState<number>(null);
  const [overrideLabour, setOverrideLabour] = useState<boolean>(false);

  const isWarrantyProduct = product?.productType === ProductType.warrantiClassBased;
  const insurerListApi = useApi(
    {
      action: Insurer.list,
      body: {
        productId: [product?.id],
      },
      wait: !product?.id,
    },
    [product?.id]
  );
  const insurerAdjudications = useMemo(
    () =>
      Insurer.createInsurerAdjudications(
        insurerListApi?.payload?.data?.map((it) => new Insurer(it)),
        Currency.CAD
      ),
    [insurerListApi?.payload?.data]
  );

  const canViewClaimJobDocuments =
    isClaimAdmin ||
    permissions?.UPLOAD_CLAIMJOBDOCUMENT ||
    permissions?.VIEW_CLAIMJOBDOCUMENT ||
    permissions?.DOWNLOAD_CLAIMJOBDOCUMENT ||
    permissions?.DELETE_CLAIMJOBDOCUMENT;

  const canViewClaimJobComments =
    isClaimAdmin ||
    permissions?.CREATE_CLAIMJOBCOMMENT ||
    permissions?.VIEW_CLAIMJOBCOMMENT ||
    permissions?.EDIT_CLAIMJOBCOMMENT ||
    permissions?.DELETE_CLAIMJOBCOMMENT;

  const canViewClaimJobItems =
    isClaimAdmin ||
    permissions?.CREATE_CLAIMJOBITEM ||
    permissions?.VIEW_CLAIMJOBITEM ||
    permissions?.EDIT_CLAIMJOBITEM ||
    permissions?.DELETE_CLAIMJOBITEM;
  //#region Api calls

  const {
    payload: claimJob,
    isExecuting: isClaimJobExecuting,
    execute: reloadClaimJob,
  } = useApi(
    {
      action: ClaimJob.find,
      body: { id: claimJobId },
      default: null,
      wait: !claimJobId || !hasAnyClaimJobPermission,
    },
    [claimJobId, hasAnyClaimJobPermission]
  );
  const insurerAdjudicationKeys = useMemo(
    () =>
      uniq([
        ...flatMap(claimJob?.claimJobItems ?? [], (it) => keys(it.insurerAdjudications)),
        ...keys(insurerAdjudications),
        ...(product?.insurers?.map((it) => it.name) ?? []),
      ]),
    [claimJob?.claimJobItems, insurerAdjudications, product?.insurers]
  );

  function getInsurerAdjudicationValues(claimJobItem?: Utils.FormData<Model.IClaimJobItem>) {
    return insurerAdjudicationKeys.map((insurerKey) => claimJobItem?.insurerAdjudications?.[insurerKey]);
  }

  function getInsurerAdjudicationTotal(claimJobItem?: Utils.FormData<Model.IClaimJobItem>) {
    return getInsurerAdjudicationValues(claimJobItem).reduce((sum, value) => sum + (value?.amount ?? 0), 0);
  }

  function hasMissingInsurerAdjudicationAmount(claimJobItem?: Utils.FormData<Model.IClaimJobItem>) {
    return getInsurerAdjudicationValues(claimJobItem).some((value) => !isValidValue(value?.amount));
  }

  function hasMismatchedInsurerAdjudicationTotal(claimJobItem?: Utils.FormData<Model.IClaimJobItem>) {
    return Math.abs(getInsurerAdjudicationTotal(claimJobItem) - (claimJobItem?.adjudication ?? 0)) > 0.009;
  }

  const cloneApi = useApi({
    action: ClaimJob.clone,
    callback: () => {
      reloadClaim();
    },
    wait: true,
  });

  const {
    payload: comments,
    isExecuting: isLoadingComments,
    execute: reloadComments,
  } = useApi(
    {
      action: ClaimJobComment.list,
      body: { claimJobId },
      wait: !claimJobId || !canViewClaimJobComments,
      keepResponse: true,
    },
    [claimJobId, canViewClaimJobComments]
  );

  const { payload: claimJobPaymentItems, execute: reloadPaymentItems } = useApi(
    {
      action: ClaimJobPaymentItem.list,
      body: { claimJobId: [claimJobId] },
      wait: !claimJobId || !canViewClaimJobItems,
      keepResponse: true,
    },
    [claimJobId, canViewClaimJobItems]
  );

  function reload() {
    if (hasAnyClaimJobPermission) {
      reloadClaimJob((body) => body);
    }
    if (canViewClaimJobComments) {
      reloadComments((body) => body);
    }
    if (canViewClaimJobItems) {
      reloadPaymentItems((body) => body);
    }
    setEmailAction(null);
    reloadClaim();
    window.location.reload(); // TODO: Temp fix. Remove.
  }
  //#endregion

  //#region Data editing
  const [action, setAction] = useState<'edit' | 'delete' | 'clone' | 'expanded' | 'compressed'>();
  const form = useForm<Utils.FormData<Model.IClaimJob>>(
    {
      default: {
        ...claimJob,
        claimId: claimJob?.claimId ?? claim?.id,
        jobNumber: claimJob?.jobNumber || (claim?.claimJobs?.length || 0) + 1,
        statusCode: claimJob?.statusCode || ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS,
        ownerId: !claimJob?.id ? userId : claimJob?.ownerId,
      },
      onSubmit: async ({ statusCode, ownerId, ...formJob }) => {
        const finalJob = {
          ...formJob,
          ownerId,
          statusCode,
          claimJobItems: formJob?.claimJobItems?.filter(
            (it) => it.claimJobItemPayee?.payeeId || it.claimJobItemPayee?.dealerId || it.claimJobItemPayee?.clientId
          ),
        };
        const jobResponse = await claimJobSaveApi(omit(finalJob, 'claimType'));
        const savedClaimJobId = finalJob?.id || jobResponse.payload;

        if (savedClaimJobId) reloadClaim();
        setAction('expanded');
      },
      validation: (data, errors) => {
        //TODO check if all documents are valid
        // Claim jobs
        if (!data.claimType?.id) set(errors, 'claimType.id', lang.mustNotBeEmpty);
        if (
          data.claimJobItems?.some(
            (it) => it.hoursWorked === null || (it.hoursWorked < 0 && it.jobItemTypeCode === LineItem.labour)
          )
        )
          set(errors, 'claimJobItems.hoursWorked', lang.mustBeMoreThanAmount.replace('{amount}', '0'));
        if (data.claimJobItems?.some((it) => !it.claimJobItemPayee && it.jobItemTypeCode === LineItem.deductible))
          set(errors, 'claimJobItems.claimJobItemPayee', lang.mustNotBeEmpty);
        if (data.claimJobItems?.some((it) => it.gstHst > 1))
          set(
            errors,
            'claimJobItems.gstHst',
            lang.mustBeEqualOrLessThanAmount.replace('{amount}', `Adjusted amt amount`)
          );
        if (
          data.claimJobItems?.some(
            (it) =>
              it.insurerAdjudications &&
              values(it.insurerAdjudications).reduce((sum, v) => sum + (v?.amount ?? 0), 0) > (it.adjudication ?? 0)
          )
        ) {
          set(
            errors,
            'claimJobItems.insurerAdjudications',
            lang.insurerAdjustedAmtMustBeEqualOrLessThanAmount.replace('{amount}', 'Adjusted amt amount')
          );
        }
        if (data.claimJobItems?.some((it) => it.provincialSalesTax > 1))
          set(
            errors,
            'claimJobItems.provincialSalesTax',
            lang.mustBeEqualOrLessThanAmount.replace('{amount}', `Adjusted amt amount`)
          );
        if (
          data.claimJobItems?.some(
            (it) =>
              !(
                it.paymentMethodCode ??
                it.claimJobItemPayee?.dealer?.paymentMethodCode ??
                it.claimJobItemPayee?.payee?.paymentMethodCode ??
                it.claimJobItemPayee?.client?.paymentMethodCode
              )
          )
        )
          set(errors, 'claimJobItems.paymentMethodCode', lang.missingPayeePaymentMethods);
      },
    },
    [claimJob, claim]
  );

  function isSamePayee(
    payee1?: Utils.FormData<Model.IClaimJobItemPayee>,
    payee2?: Utils.FormData<Model.IClaimJobItemPayee>
  ) {
    return payee1?.clientId
      ? payee1?.clientId === payee2?.clientId
      : payee1?.dealerId
        ? payee1?.dealerId === payee2?.dealerId
        : payee1?.payeeId
          ? payee1?.payeeId === payee2?.payeeId
          : false;
  }
  function updateJobItem(data?: Utils.FormData<Model.IClaimJobItem>, index?: number) {
    let jobItem = new ClaimJobItem(data ?? {}).toJSON();
    const claimJobItems = form.data?.claimJobItems || [];
    const currencyKey = jobItem?.currency === Currency.CAD ? Currency.CAD : Currency.USD;

    const groupedClaimJobItems: Dictionary<Utils.FormData<Model.IClaimJobItem>[]> = groupBy(
      claimJobItems,
      (it) => it.currency
    );
    const targetClaimJobItems = groupedClaimJobItems[currencyKey] || [];
    for (const claimJobItem of targetClaimJobItems) {
      if (!claimJobItem.paymentMethodCode) {
        claimJobItem.paymentMethodCode =
          targetClaimJobItems?.find(
            (it) => isSamePayee(claimJobItem.claimJobItemPayee, it.claimJobItemPayee) && it.paymentMethodCode
          )?.paymentMethodCode ??
          claimJobItem.claimJobItemPayee?.dealer?.paymentMethodCode ??
          claimJobItem.claimJobItemPayee?.payee?.paymentMethodCode ??
          claimJobItem.claimJobItemPayee?.client?.paymentMethodCode;
      }
    }

    if (jobItem?.jobItemTypeCode === LineItem.part) {
      const quantity = Number(jobItem?.quantity ?? 0);
      const baseCost = Number(jobItem?.baseCost ?? 0);

      if (quantity === 0) {
        jobItem = { ...jobItem, quantity: 0, baseCost: 0, correction: 0 };
      } else {
        const correction = baseCost * quantity;
        jobItem = { ...jobItem, quantity, baseCost, correction };
      }
    }

    if (jobItem?.jobItemTypeCode === LineItem.labour) {
      const labourRate = !jobItem?.labourRate
        ? (jobItem?.claimJobItemPayee?.payee?.labourRate ?? jobItem?.claimJobItemPayee?.dealer?.labourRate)
        : jobItem?.labourRate;

      const correction = labourRate ? labourRate * jobItem?.hoursWorked : 0.0;
      jobItem = { ...jobItem, correction, labourRate };
    }

    if (isValidValue(index)) {
      targetClaimJobItems[index] = jobItem;
    } else {
      targetClaimJobItems.push(jobItem);
    }

    groupedClaimJobItems[currencyKey] = targetClaimJobItems;

    const mergedItems = Object.values(groupedClaimJobItems).flat();

    form.update({ claimJobItems: mergedItems });
  }

  function updateMultipleJobItems(
    claimJobItemPayee: Utils.FormData<Model.IClaimJobItemPayee>,
    data: Utils.FormData<Omit<Model.IClaimJobItem, 'claimJobItemPayee'>>
  ) {
    const claimJobItems = [];
    for (const claimJobItem of form.data?.claimJobItems || []) {
      if (isSamePayee(claimJobItemPayee, claimJobItem.claimJobItemPayee)) {
        claimJobItems.push({ ...claimJobItem, ...data });
      } else {
        claimJobItems.push(claimJobItem);
      }
    }
    form.update({ claimJobItems });
  }
  function removeJobItem(data: Utils.FormData<Model.IClaimJobItem>, index: number) {
    const currentGroupedClaimJobItems: Dictionary<Utils.FormData<Model.IClaimJobItem>[]> = groupBy(
      form.data?.claimJobItems || [],
      (it) => it.currency
    );

    const targetClaimJobItems = currentGroupedClaimJobItems[data.currency] || [];

    if (isValidValue(index)) {
      targetClaimJobItems.splice(index, 1);
    }

    const mergedItems = Object.values(currentGroupedClaimJobItems).flat();

    form.update({ claimJobItems: mergedItems });
  }
  function createDefaultJobItems() {
    const claimJobItems: Array<Utils.FormData<Model.IClaimJobItem>> = [];
    const labourItems = 1;
    const partItems = 3;

    const createJobItem = (
      jobItemTypeCode: LineItem,
      correction: number,
      insurerAdjudications: Model.IInsurerAdjudication
    ) =>
      new ClaimJobItem({
        jobItemTypeCode,
        statusCode: PaymentStatus.unpaid,
        currency: Currency.CAD,
        adjudication: 0.0,
        paymentMethodCode: undefined,
        insurerAdjudications: insurerAdjudications,
        correction: correction,
      }).toJSON();

    if (isWarrantyProduct) {
      claimJobItems.push(createJobItem(LineItem.deductible, contract.deductible, insurerAdjudications));
    }
    for (let i = 0; i < labourItems; i++) {
      claimJobItems.push(createJobItem(LineItem.labour, 0.0, insurerAdjudications));
    }
    for (let i = 0; i < partItems; i++) {
      claimJobItems.push(createJobItem(LineItem.part, 0.0, insurerAdjudications));
    }

    form.update({ claimJobItems });
  }
  //#endregion

  //#region Variables
  const claimType = useMemo(
    () => claimTypes?.find((it) => it.id === form?.data?.claimType?.id),
    [claimTypes, form?.data?.claimType?.id]
  );

  const isEditLoading = !claimTypes?.length || isClaimJobExecuting || !claimJob?.claimJobItems;
  const uploadedDocuments = form?.data?.claimJobDocuments?.filter((doc) => doc?.id) || [];
  const uploadableDocuments = useMemo(() => {
    const docs = claimType?.allClaimTypeDocuments?.filter((it) => it.version === claimJob?.claimTypeVersion);
    if (isEmpty(docs)) {
      return claimType?.claimTypeDocuments || [];
    } else {
      return docs;
    }
  }, [claimType, claimJob]);
  //#endregion

  //#region Reports
  const claimTypeReport = useMemo(
    () => (claimType?.claimTypeReportSettings?.length ? claimType.claimTypeReportSettings[0] : null),
    [claimType]
  );
  const report = useMemo(() => {
    const firstReport = form.data?.claimJobReports && form.data?.claimJobReports[0];
    return claimTypeReport?.buildReportObject(firstReport?.productReport || {});
  }, [form.data, claimTypeReport]);
  //#endregion

  function getJobItemDefaultValues({
    jobItemTypeCode,
    claimJobItemPayee,
  }: {
    jobItemTypeCode?: string;
    claimJobItemPayee?: Utils.FormData<Model.IClaimJobItemPayee>;
  }) {
    const jobs = form.data?.claimJobItems ?? [];

    const payee = claimJobItemPayee?.dealer ?? claimJobItemPayee?.payee;
    const tax = product.productTaxes?.find((it) => {
      return it.provinceCode === (payee?.provinceCode || claimJobItemPayee?.client?.provinceCode);
    });
    const paymentMethodCode =
      jobs?.find((it) => isSamePayee(claimJobItemPayee, it.claimJobItemPayee) && it.paymentMethodCode)
        ?.paymentMethodCode ??
      payee?.paymentMethodCode ??
      claimJobItemPayee?.client?.paymentMethodCode;
    return {
      correction: jobItemTypeCode === LineItem.deductible && isWarrantyProduct ? contract?.deductible : 0.0,
      gstHst: tax?.claimsGstHst || 0.0,
      provincialSalesTax: tax?.claimsProvincialSalesTax || 0.0,
      labourRate: payee?.labourRate,
      paymentMethodCode: paymentMethodCode,
      statusCode: PaymentStatus.unpaid,
    };
  }

  useEffect(() => {
    if (claimJob && isEmpty(claimJob.claimJobItems)) {
      createDefaultJobItems();
    }
  }, [claimJob, isWarrantyProduct]);

  const mapClaimJobDataPoints = (claimTypeDataPoints: Utils.FormData<Model.IClaimTypeDataPoint[]>) =>
    sortBy(
      claimTypeDataPoints?.map((it) => ({
        claimTypeDataPointId: it.id,
        claimTypeId: it.claimTypeId,
        name: it.name,
        type: it.type,
        version: it.version,
        isRequired: it.isRequired,
        value: undefined,
      })),
      (it) => it.claimTypeDataPointId
    );
  const mapClaimJobItems = (claimTypeJobItemTemplates: Utils.FormData<Model.IClaimTypeJobItemTemplate[]>) => {
    const mappedItems = claimTypeJobItemTemplates?.map(
      (it) =>
        ({
          lossCodes: it.lossCodes,
          lossCode: it.lossCodes?.length === 1 ? first(it.lossCodes)?.code : undefined,
          claimTypeId: it.claimTypeId,
          correction: it.correction,
          adjudication: it.correction,
          jobItemTypeCode: it.jobItemTypeCode,
          statusCode: PaymentStatus.unpaid,
          baseCost: 0,
          quantity: it.lossCodes?.length === 1 && it.jobItemTypeCode === LineItem.part ? 1 : 0,
          hoursWorked: 0,
          labourRate: 0,
          currency: Currency.CAD,
          insurerAdjudications: insurerAdjudications,
          version: it.version,
          templateId: it.id,
        }) as any
    );

    if (!isEmpty(mappedItems) && isWarrantyProduct) {
      const deductibleCorrection = form.data.claim?.contract?.deductible ?? 0.0;
      mappedItems.push({
        jobItemTypeCode: LineItem.deductible,
        correction: deductibleCorrection,
        adjudication: deductibleCorrection,
        gstHst: 0.0,
        provincialSalesTax: 0.0,
        total: deductibleCorrection,
        currency: Currency.CAD,
        statusCode: PaymentStatus.unpaid,
        insurerAdjudications: insurerAdjudications,
      } as any);
    }

    return mappedItems;
  };

  //#region Status flow
  const emailTemplatesApi = useApi({ action: ClaimJobEmailTemplate.list });
  const emailTemplates = emailTemplatesApi.payload?.data;

  const statusInfoApi = useApi({ action: ClaimJob.getStatusFlowAndRequirements });
  const statusInfo = statusInfoApi.payload;
  const changeJobStatusForm = useForm<{
    claimJobId: number;
    nextStatus: Model.Enum.ClaimJobStatusCode;
    ownerId?: number;
    claimJobEmailTemplate?: Model.IClaimJobEmailTemplate;
  }>(
    {
      default: {
        claimJobId: claimJob?.id,
        ownerId: claimJob?.ownerId,
        claimJobEmailTemplate: null,
      },
      onSubmit: async ({ ownerId, nextStatus, claimJobEmailTemplate }) => {
        const hasStatusChanged = nextStatus && nextStatus !== claimJob?.statusCode;
        const isApprovingJob = [
          ClaimJobStatusCode.OPEN_APPROVED_WAITING_ON_INVOICE,
          ClaimJobStatusCode.OPEN_APPROVED_INVOICE_REVIEW_SERVICE,
          ClaimJobStatusCode.OPEN_APPROVED_INVOICE_RECEIVED,
          ClaimJobStatusCode.OPEN_APPROVED_TO_PAY_BY_ACCOUNTING,
        ].includes(nextStatus as ClaimJobStatusCode);

        if (isApprovingJob && canEditInsurerAdjudications && !isEmpty(insurerAdjudicationKeys)) {
          if (claimJob?.claimJobItems?.some((it) => hasMissingInsurerAdjudicationAmount(it))) {
            addLog({ error: lang.insurerAdjudicationsMustBeFilledBeforeApproval });
            return;
          }

          if (claimJob?.claimJobItems?.some((it) => hasMismatchedInsurerAdjudicationTotal(it))) {
            addLog({ warning: lang.insurerAdjudicationTotalsMustMatchAdjustedAmt });
            return;
          }
        }

        if (hasStatusChanged && claimJob && ownerId) {
          const res = await claimJobSetStatusApi({
            claimJobId: claimJob.id,
            status: nextStatus,
            ownerId,
            claimJobEmailTemplate,
          });
          if (res.status === HttpStatus.ok) {
            if (nextStatus === ClaimJobStatusCode.OPEN_APPROVED_TO_PAY_BY_ACCOUNTING) {
              const claimJobItemAggregates = ClaimJob.aggregates(new ClaimJob(claimJob));

              for (const claimJobAggregate of claimJobItemAggregates || []) {
                await claimJobPaymentItemSaveApi({
                  createdById: userId,
                  ...ClaimJobPaymentItem.convertClaimJobAggregateToClaimJobPaymentItem(claimJobAggregate),
                });
              }
            }

            addLog({ success: lang.claimStatusSuccesfullyChanged });
            reload();
          } else {
            addLog({ error: res.message });
          }
        }
      },
      validation: (data, errors) => {
        if (data.claimJobEmailTemplate) {
          if (
            !data.claimJobEmailTemplate.additionalRecipientEmails?.some((it) => it) &&
            !data.claimJobEmailTemplate.recipientsWithRole?.some((it) => it.id) &&
            !data.claimJobEmailTemplate.sendToClaimOwner &&
            !data.claimJobEmailTemplate.sendToClient &&
            !data.claimJobEmailTemplate.sendToContactEmail
          )
            set(errors, 'claimJobEmailTemplate.additionalRecipientEmails', lang.mustNotBeEmpty);
          if (
            data.claimJobEmailTemplate.additionalRecipientEmails?.length &&
            data.claimJobEmailTemplate.additionalRecipientEmails?.some((it) => !isEmail(it))
          )
            set(errors, 'claimJobEmailTemplate.additionalRecipientEmails', lang.invalidEmailFormatRequires);
          if (!data.claimJobEmailTemplate.subject) set(errors, 'claimJobEmailTemplate.subject', lang.mustNotBeEmpty);
          if (!data.claimJobEmailTemplate.name) set(errors, 'claimJobEmailTemplate.name', lang.mustNotBeEmpty);
          if (!data.claimJobEmailTemplate.senderEmail)
            set(errors, 'claimJobEmailTemplate.senderEmail', lang.mustNotBeEmpty);
          if (!isEmailDomain(data.claimJobEmailTemplate.senderEmail, 'goautoshield.com'))
            set(
              errors,
              'claimJobEmailTemplate.senderEmail',
              lang.mustBeEmailDomain.replace('{domain}', 'goautoshield.com')
            );
          if (!data.claimJobEmailTemplate.senderName)
            set(errors, 'claimJobEmailTemplate.senderName', lang.mustNotBeEmpty);
          if (!data.claimJobEmailTemplate.htmlBody) set(errors, 'claimJobEmailTemplate.htmlBody', lang.mustNotBeEmpty);
        }
      },
    },
    [claimJob]
  );

  const resetStatus = async () => {
    const hasStatusChanged = claimJob?.statusCode !== ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS;
    if (hasStatusChanged && claimJob) {
      const res = await claimJobResetStatusAndDeleteAssociatedPaymentItemsApi({
        id: claimJob.id,
      });

      if (res.status === HttpStatus.ok) {
        addLog({ success: lang.claimStatusSuccesfullyChanged });
        reload();
      } else addLog({ error: res.message });
    }
  };

  const nextStatus = changeJobStatusForm.data.nextStatus;
  const currentStatus = claimJob?.statusCode || ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS;
  const isStatusChanged = nextStatus && nextStatus !== currentStatus;
  //#endregion

  const isAnyClaimJobItemPaid = claimJob?.claimJobItems?.some((it) => it.statusCode === PaymentStatus.paid);
  const isPaymentMethodLocked =
    claimJob?.statusCode === ClaimJobStatusCode.OPEN_APPROVED_TO_PAY_BY_ACCOUNTING ||
    claimJob?.statusCode === ClaimJobStatusCode.OPEN_PAID_PAYMENT_SENT ||
    claimJob?.statusCode === ClaimJobStatusCode.DENIED_DECLINED ||
    claimJob?.statusCode === ClaimJobStatusCode.PAID_CLOSED_PAID ||
    claimJob?.statusCode === ClaimJobStatusCode.CANCELED_VOID;

  const isJobEditDisabled =
    claimJob?.statusCode === ClaimJobStatusCode.OPEN_ACTIVE_PENDING ||
    claimJob?.statusCode === ClaimJobStatusCode.OPEN_APPROVED_INVOICE_RECEIVED ||
    claimJob?.statusCode === ClaimJobStatusCode.OPEN_APPROVED_INVOICE_REVIEW_SERVICE ||
    claimJob?.statusCode === ClaimJobStatusCode.OPEN_APPROVED_WAITING_ON_INVOICE ||
    isPaymentMethodLocked;

  const isClaimJobItemStatusChangeable =
    claim?.statusCode === ClaimStatusCode.PAID_CLOSED_PAID ||
    claimJob?.statusCode === ClaimJobStatusCode.PAID_CLOSED_PAID;

  const isPaymentAlreadyCreated = !!claimJobPaymentItems?.data?.some((it) => it.claimJobPaymentId);

  const [isEditingTax, setIsEditingTax] = useState<boolean>(false);
  useEffect(() => {
    if (isEditingTax) addLog({ warning: lang.claimJobItemTaxEditWarning });
  }, [isEditingTax]);

  return {
    product,
    comments,
    claimJob,
    claimTypes,
    isLoadingComments,
    isPaymentAlreadyCreated,
    reloadComments,
    cloneApi,
    uploadedDocuments,
    uploadableDocuments,
    claim,
    claimTypeReport,
    contract,
    form,
    claimType,
    currentStatus,
    changeJobStatusForm,
    isEditingTax,
    setIsEditingTax,
    emailAction,
    setEmailAction,
    isStatusChanged,
    updateJobItem,
    removeJobItem,
    reload,
    isEditable,
    permissions,
    isEditLoading,
    emailTemplates,
    statusInfo,
    action,
    setAction,
    isClaimAdmin,
    isClaimOwner,
    report,
    updateMultipleJobItems,
    getJobItemDefaultValues,
    isWarrantyProduct,
    reloadClaim,
    isClaimJobEditableByStatus: isClaimJobEditableByStatus || isJobEditDisabled,
    isClaimJobItemEditable: isClaimJobItemEditable || isJobEditDisabled,
    isClaimJobClonable: isClaimJobItemEditable,
    isClaimJobItemStatusChangeable,
    isAnyClaimJobItemPaid,
    isPaymentMethodLocked,
    mapClaimJobDataPoints,
    mapClaimJobItems,
    newPayeeId,
    setNewPayeeId,
    hasAnyClaimJobPermission,
    canViewClaimJobComments,
    canViewClaimJobDocuments,
    canViewClaimJobItems,
    resetStatus,
    isSamePayee,
    overrideLabour,
    setOverrideLabour,
    canEditAdjustedAmt,
    canEditInsurerAdjudications,
    payeeRestriction,
  };
}

const { useProvider: useClaimJobEdit, Provider: ClaimJobEditProvider } = ProviderBuilder(useClaimJobEditCore);

export { ClaimJobEditProvider, useClaimJobEdit };

export function useClaimEditJobComments() {
  const {
    form: jobForm,
    reloadComments,
    comments,
    isLoadingComments,
    claim,
    permissions,
    isClaimAdmin,
    isClaimOwner,
  } = useClaimJobEdit();
  const { lang } = getLocales();
  const auth = useAuthStore();
  const [action, setAction] = useState<'edit' | 'delete' | 'expanded' | 'compressed'>();
  const form = useForm<Utils.FormData<Model.IClaimJobComment>>({
    default: {
      claimJobId: jobForm.data.id,
      userId: auth.userId,
      comment: undefined,
    },
    validation: (data, errors) => {
      if (!data.comment) errors.comment = lang.required;
    },
    onSubmit: async (data) => {
      const res = await claimJobCommentSaveApi({
        ...data,
        modifiedAtUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
      });
      if (res.status !== HttpStatus.ok) addLog({ error: res.message });
      else {
        reloadComments((body) => body);
      }
    },
  });
  useEffect(() => {
    if (form.isSubmitted) {
      setAction(null);
      form.reload();
    }
  }, [form.isSubmitted]);

  useEffect(() => {
    if (jobForm.data.id)
      reloadComments({
        claimJobId: jobForm.data.id,
      });
    setAction(null);
    form.reload();
  }, [jobForm.data.id]);

  const deleteApi = useApi({
    action: claimJobCommentDeleteApi,
    callback: () => {
      setAction(null);
      form.reload();
      reloadComments((body) => body);
    },
    wait: true,
  });
  return {
    hasJob: !!jobForm.data.id,
    form,
    deleteApi,
    isLoadingComments,
    comments,
    action,
    setAction,
    permissions,
    isClaimAdmin,
    isClaimJobCommentEditableByStatus: claim?.statusCode === ClaimStatusCode.CANCELED_VOID,
    canViewComments: permissions?.VIEW_CLAIMJOBCOMMENT || isClaimAdmin,
    canDeleteComments: (isClaimOwner && permissions?.DELETE_CLAIMJOBCOMMENT) || isClaimAdmin,
    canAddComments: (isClaimOwner && permissions?.CREATE_CLAIMJOBCOMMENT) || isClaimAdmin,
    canEditComments: (isClaimOwner && permissions?.EDIT_CLAIMJOBCOMMENT) || isClaimAdmin,
    currentUserId: auth?.userId,
  };
}

type ClaimEditJobPayeeItemCoreProps = { countryCode: Model.Enum.CountryCode };
export function useClaimEditJobPayeeItemCore(props: ClaimEditJobPayeeItemCoreProps) {
  const { contract, permissions, payeeRestriction } = useClaimEdit();

  const defaultPayeeLimit = 10;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>(undefined);
  const [offset, setOffset] = useState<number>(defaultOffset);
  const [limit, setLimit] = useState<number>(defaultPayeeLimit);

  const payeeTypeApi = useApi({ action: PayeeType.list });
  const payeApi = useApi({ action: Payee.list });
  const dealerListApi = useApi(
    {
      action: Dealer.list,
      body: { distributorId: contract?.dealer?.representativeId ? [contract?.dealer?.representativeId] : undefined },
      wait: !contract?.dealer?.representativeId,
    },
    [contract?.dealer?.representativeId]
  );

  const payeeList = useMemo(() => {
    const data: ClaimJobItemPayee[] = [];

    if (payeeRestriction && offset === 0) {
      if (contract?.client) {
        data.push(
          new ClaimJobItemPayee({
            client: contract.client,
            clientId: contract.client?.id,
            payeeType: payeeTypeApi.payload?.data?.find((it) => it.id === PayeeType.CLIENT_ID),
          })
        );
      }

      if (contract?.dealer) {
        data.push(
          ...(dealerListApi?.payload?.data?.map(
            (dealer) =>
              new ClaimJobItemPayee({
                dealer,
                dealerId: dealer?.id,
                payeeType: payeeTypeApi.payload?.data?.find((it) => it.id === PayeeType.DEALER_ID),
              })
          ) || [])
        );
      }
    }

    data.push(
      ...(payeApi?.payload?.data?.map(
        (payee) =>
          new ClaimJobItemPayee({
            payee,
            payeeId: payee?.id,
            payeeType: payeeTypeApi.payload?.data?.find((it) => it.id === payee?.payeeTypeId),
          })
      ) || [])
    );

    return data;
  }, [
    payeApi?.payload?.data,
    contract?.dealer?.id,
    contract?.client?.id,
    payeeTypeApi?.payload?.data,
    dealerListApi?.payload?.data,
  ]);

  const filteredPayees = useMemo(() => {
    const filteredPayees = payeeList?.filter((it) => {
      const cc = it?.payee?.countryCode ?? it?.client?.countryCode ?? it?.dealer?.countryCode;
      return normalizeCountryCode(cc) === props.countryCode; // Filter based on countryCode
    });

    setIsLoading(false);
    return filteredPayees;
  }, [props.countryCode, payeeList]);

  // Use client side pagination for select input
  const searchedPayees = useMemo(() => {
    if (search) {
      setOffset(0);
      return filteredPayees.filter((p) => {
        const payee = p.client ?? p.dealer ?? p.payee;
        return payee?.displayInfo?.title?.toLowerCase()?.includes(search.toLowerCase());
      });
    } else return filteredPayees;
  }, [filteredPayees, search]);

  const paginatedPayees = useMemo(() => {
    const applyPagination = (list: ClaimJobItemPayee[], limit: number, offset: number) => {
      const start = offset * limit;
      const end = start + limit;
      return list.slice(start, end);
    };
    return applyPagination(searchedPayees, limit, offset);
  }, [searchedPayees, limit, offset]);

  return {
    setSearch,
    search,
    setOffset,
    setLimit,
    offset,
    payeeList,
    count: payeApi?.payload?.count ?? 0,
    limit,
    payeeTypeApi,
    permissions,
    payeApi,
    paginatedPayees,
    searchedPayees,
    isLoading,
  };
}

const { useProvider: useClaimEditJobPayeeItem, Provider: ClaimEditJobPayeeItemProvider } =
  ProviderBuilder(useClaimEditJobPayeeItemCore);
export { ClaimEditJobPayeeItemProvider, useClaimEditJobPayeeItem };
